import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Divider,
  CircularProgress,
  Avatar,
  alpha,
  Chip,
} from "@mui/material";
import {
  Star,
  StarBorder,
  Close,
  RateReview,
  CheckCircle,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import RestaurantRatingServices from "../../../services/RestaurantRatingService";
import { isValidPhoneNumber, sanitizePhoneNumber } from "../../../utils/phoneUtils";

const STAR_COUNT = 5;

const ratingLabels = {
  1: "Poor",
  2: "Fair",
  3: "Good",
  4: "Very Good",
  5: "Excellent",
};

const StarRating = ({
  value,
  hoverValue,
  onHover,
  onLeave,
  onClick,
  size = 40,
}) => (
  <Stack direction="row" spacing={0.5}>
    {Array.from({ length: STAR_COUNT }, (_, i) => {
      const starIndex = i + 1;
      const filled = starIndex <= (hoverValue || value);
      return (
        <IconButton
          key={starIndex}
          onMouseEnter={() => onHover(starIndex)}
          onMouseLeave={onLeave}
          onClick={() => onClick(starIndex)}
          sx={{
            p: 0.25,
            color: filled ? "#f59e0b" : alpha("#f59e0b", 0.3),
            "&:hover": { bgcolor: "transparent", transform: "scale(1.2)" },
            transition: "transform 0.15s ease, color 0.15s ease",
          }}
        >
          {filled ? (
            <Star sx={{ fontSize: size }} />
          ) : (
            <StarBorder sx={{ fontSize: size }} />
          )}
        </IconButton>
      );
    })}
  </Stack>
);

const AverageStars = ({ value, size = 18 }) => (
  <Stack direction="row" spacing={0.25}>
    {Array.from({ length: STAR_COUNT }, (_, i) => {
      const filled = i + 1 <= Math.round(value);
      return filled ? (
        <Star key={i} sx={{ fontSize: size, color: "#f59e0b" }} />
      ) : (
        <StarBorder key={i} sx={{ fontSize: size, color: "#f59e0b" }} />
      );
    })}
  </Stack>
);

const RatingDialog = ({ open, onClose, companyId, restaurantName }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loadingRatings, setLoadingRatings] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [recentReviews, setRecentReviews] = useState([]);
  const isCustomerPhoneInvalid =
    customerPhone.trim().length > 0 && !isValidPhoneNumber(customerPhone);

  useEffect(() => {
    if (open && companyId) {
      fetchRatings();
    }
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  const fetchRatings = async () => {
    setLoadingRatings(true);
    try {
      const res = await RestaurantRatingServices.getByCompany({ companyId });
      if (res.success && res.data?.view?.data) {
        const data = res.data.view.data;
        if (data.length > 0) {
          const company = data[0]?.Company || {};
          setAverageRating(parseFloat(company.averageRating) || 0);
          setTotalRatings(parseInt(company.totalRatings) || 0);
          const reviews = data
            .filter((r) => r.review)
            .slice(0, 5)
            .map((r) => ({
              name: r.customerName || "Guest",
              rating: r.rating || 0,
              review: r.review,
            }));
          setRecentReviews(reviews);
        }
      }
    } catch (_) {
      // silently skip
    } finally {
      setLoadingRatings(false);
    }
  };

  const resetForm = () => {
    setRating(0);
    setHoverRating(0);
    setCustomerName("");
    setCustomerPhone("");
    setReview("");
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    if (!rating) {
      toast.warning("Please select a star rating.");
      return;
    }
    if (!customerName.trim()) {
      toast.warning("Please enter your name.");
      return;
    }
    if (!customerPhone.trim()) {
      toast.warning("Please enter your phone number.");
      return;
    }
    if (!isValidPhoneNumber(customerPhone)) {
      toast.warning("Please enter a valid phone number (10 to 15 digits).");
      return;
    }

    setSubmitting(true);
    const res = await RestaurantRatingServices.create({
      companyId,
      customerName: customerName.trim(),
      customerPhone: sanitizePhoneNumber(customerPhone),
      rating,
      review: review.trim(),
      isApproved: true,
    });
    setSubmitting(false);

    if (res.success) {
      setSubmitted(true);
      fetchRatings();
    } else {
      toast.error(res.message || "Failed to submit rating. Please try again.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
          color: "white",
          p: 3,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "white",
            bgcolor: alpha("#fff", 0.1),
            "&:hover": { bgcolor: alpha("#fff", 0.2) },
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
          <Avatar
            sx={{
              bgcolor: alpha("#f59e0b", 0.2),
              width: 44,
              height: 44,
            }}
          >
            <RateReview sx={{ color: "#f59e0b" }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              Rate Your Experience
            </Typography>
            {restaurantName && (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {restaurantName}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Average summary */}
        {!loadingRatings && totalRatings > 0 && (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ color: "#f59e0b", lineHeight: 1 }}
            >
              {averageRating.toFixed(1)}
            </Typography>
            <Box>
              <AverageStars value={averageRating} size={16} />
              <Typography variant="caption" sx={{ opacity: 0.65 }}>
                Based on {totalRatings}{" "}
                {totalRatings === 1 ? "review" : "reviews"}
              </Typography>
            </Box>
          </Stack>
        )}
        {loadingRatings && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={14} sx={{ color: "white" }} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Loading ratings...
            </Typography>
          </Stack>
        )}
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {submitted ? (
          /* Success State */
          <Stack alignItems="center" spacing={2} py={3}>
            <Avatar
              sx={{
                bgcolor: alpha("#10b981", 0.15),
                width: 72,
                height: 72,
              }}
            >
              <CheckCircle sx={{ fontSize: 40, color: "#10b981" }} />
            </Avatar>
            <Typography variant="h6" fontWeight={700} textAlign="center">
              Thank you for your feedback!
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 300 }}
            >
              Your review helps us improve and serve you better.
            </Typography>
            <AverageStars value={rating} size={28} />
            <Chip
              label={ratingLabels[rating]}
              sx={{
                bgcolor: alpha("#f59e0b", 0.12),
                color: "#f59e0b",
                fontWeight: 600,
              }}
            />
          </Stack>
        ) : (
          /* Form State */
          <Stack spacing={3}>
            {/* Star selector */}
            <Box textAlign="center">
              <Typography
                variant="body2"
                color="text.secondary"
                mb={1.5}
                fontWeight={500}
              >
                How would you rate us?
              </Typography>
              <Box display="flex" justifyContent="center">
                <StarRating
                  value={rating}
                  hoverValue={hoverRating}
                  onHover={setHoverRating}
                  onLeave={() => setHoverRating(0)}
                  onClick={setRating}
                  size={42}
                />
              </Box>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  color: "#f59e0b",
                  mt: 1,
                  minHeight: "1.4em",
                  visibility:
                    (hoverRating || rating) > 0 ? "visible" : "hidden",
                }}
              >
                {ratingLabels[hoverRating || rating] || " "}
              </Typography>
            </Box>

            <Divider />

            {/* Fields */}
            <TextField
              label="Your Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              fullWidth
              required
              size="small"
              variant="outlined"
            />
            <TextField
              label="Phone Number"
              value={customerPhone}
              onChange={(e) =>
                setCustomerPhone(sanitizePhoneNumber(e.target.value).slice(0, 15))
              }
              fullWidth
              required
              size="small"
              variant="outlined"
              error={isCustomerPhoneInvalid}
              helperText={
                isCustomerPhoneInvalid
                  ? "Enter a valid phone number (10-15 digits)"
                  : " "
              }
              inputProps={{ inputMode: "numeric", maxLength: 15 }}
            />
            <TextField
              label="Your Review (optional)"
              value={review}
              onChange={(e) => setReview(e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
              variant="outlined"
              placeholder="Share your experience..."
            />

            {/* Recent reviews */}
            {recentReviews.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  display="block"
                  mb={1}
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  Recent Reviews
                </Typography>
                <Stack spacing={1.5}>
                  {recentReviews.map((r, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: (t) => alpha(t.palette.action.hover, 0.5),
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        mb={0.5}
                      >
                        <AverageStars value={r.rating} size={14} />
                        <Typography variant="caption" fontWeight={600}>
                          {r.name}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {r.review}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        {submitted ? (
          <Button
            onClick={onClose}
            fullWidth
            variant="contained"
            sx={{
              borderRadius: 2,
              py: 1.25,
              background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #16213e 0%, #0f3460 100%)",
              },
            }}
          >
            Close
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            fullWidth
            variant="contained"
            disabled={submitting || rating === 0 || isCustomerPhoneInvalid}
            sx={{
              borderRadius: 2,
              py: 1.25,
              background:
                rating > 0
                  ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                  : undefined,
              "&:hover": {
                background:
                  rating > 0
                    ? "linear-gradient(135deg, #d97706 0%, #b45309 100%)"
                    : undefined,
              },
              fontWeight: 700,
              fontSize: "0.95rem",
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={18} sx={{ color: "inherit", mr: 1 }} />
                Submitting...
              </>
            ) : (
              "Submit Review"
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RatingDialog;
